/**
 * Place地図ページ
 *
 * Google Maps「保存した場所」風のフルスクリーン地図
 * - 常時表示のBottomSheetで一覧/詳細を切替
 * - マーカータップで詳細表示 + シートに隠れる場合はパン
 * - ×ボタンで一覧に戻りシートを最下段にスナップ
 */

import { useMap } from '@vis.gl/react-google-maps'
import { Calculator, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { getVisitedPlaces, recalcPlaceCounts } from '@/api/services/overland'
import type { Place } from '@/api/types/overland'
import { FullAccessFallback } from '@/components/shared/full-access-fallback'
import { Header } from '@/components/shared/header'
import { GoogleMapCanvas } from '@/components/shared/map/google-map-canvas'
import { DEFAULT_MAP_CENTER } from '@/components/shared/map/google-maps-config'
import { useFitBounds } from '@/components/shared/map/use-fit-bounds'
import { BottomSheet, type BottomSheetRef } from '@/components/ui/bottom-sheet'
import type { ActionSection } from '@/components/ui/custom-dropdown'
import type { SortOption } from '@/lib/place-utils'
import { PlaceListContent, type PlaceListContentRef } from '@/pages/activity/components/place-list-content'
import { PlaceSheetContent } from '@/pages/activity/components/place-sheet'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'

import { PLACE_CLUSTER_MAX_ZOOM, PlaceMarkersLayer } from './place-markers-layer'

/** シートのスナップポイント */
const SNAP_PEEK = 0.15
const SNAP_HALF = 0.5
const SNAP_FULL = 1
const SHEET_SNAP_POINTS = [SNAP_FULL, SNAP_HALF, SNAP_PEEK]

/** 選択したPlaceがクラスタに埋もれないよう、最低限このズームまで寄せる */
const SELECT_MIN_ZOOM = PLACE_CLUSTER_MAX_ZOOM + 2

// 地図の表示範囲を自動調整するコンポーネント（初回にデータが揃ったときのみ）
function MapBoundsUpdater({ places }: { places: Place[] }) {
  const points = useMemo(() => places.map((p) => ({ lat: p.latitude, lng: p.longitude })), [places])

  // fitKey を渡さないため初回のみフィットし、以降はユーザーの視点を維持する
  useFitBounds({ points, padding: 50, maxZoom: 14 })

  return null
}

/** 選択されたPlaceにフォーカス + シートに隠れないようパン */
/** マップインスタンスを親に公開するコンポーネント */
function MapRefExposer({ mapRef }: { mapRef: React.RefObject<google.maps.Map | null> }) {
  const map = useMap()
  useEffect(() => {
    mapRef.current = map
  }, [map, mapRef])
  return null
}

export function PlacesMapPage() {
  const navigate = useNavigate()
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)

  const [places, setPlaces] = useState<Place[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [sort, setSort] = useState<SortOption>('visit_count')
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null)

  const bottomSheetRef = useRef<BottomSheetRef>(null)
  const listRef = useRef<PlaceListContentRef>(null)
  const mapRef = useRef<google.maps.Map | null>(null)

  // Place一覧を取得
  const loadPlaces = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await getVisitedPlaces(sort)
      setPlaces(data)
    } catch {
      toast.error('場所一覧の取得に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [sort])

  useEffect(() => {
    if (hasFullAccess) {
      void loadPlaces()
    }
  }, [hasFullAccess, loadPlaces])

  // マーカーやリストからPlace選択 → 詳細表示 + マップパン
  const handleSelectPlace = useCallback((place: Place) => {
    setSelectedPlace(place)
    bottomSheetRef.current?.snapTo(SNAP_FULL)

    // React再レンダリング + BottomSheetスナップが完了した後にパン実行
    setTimeout(() => {
      const map = mapRef.current
      if (!map) return

      if ((map.getZoom() ?? 0) < SELECT_MIN_ZOOM) map.setZoom(SELECT_MIN_ZOOM)
      map.panTo({ lat: place.latitude, lng: place.longitude })
      // シート（ビューポートの100%）に隠れないよう、可視領域の中央にマーカーが来るまで中心を下げる
      map.panBy(0, (window.innerHeight * SNAP_FULL) / 2)
    }, 150)
  }, [])

  // 詳細から一覧に戻る（×ボタン）
  const handleBackToList = useCallback(() => {
    setSelectedPlace(null)
    // シートを最上段にスナップ（Place一覧を全画面で表示）
    bottomSheetRef.current?.snapTo(SNAP_FULL)
  }, [])

  // BottomSheetの×ボタン
  const handleSheetClose = useCallback(() => {
    if (selectedPlace) {
      // 詳細モード → 一覧に戻る
      handleBackToList()
    } else {
      // 一覧モード → シートを最下段にスナップ
      bottomSheetRef.current?.snapTo(SNAP_PEEK)
    }
  }, [selectedPlace, handleBackToList])

  // 戻るボタン
  const handleBack = useCallback(() => {
    navigate(PATHS.ACTIVITY)
  }, [navigate])

  // ドロップダウンメニュー
  const dropdownSections: ActionSection[] = [
    {
      type: 'action',
      label: '管理',
      items: [
        {
          key: 'recalc-counts',
          label: 'Placeカウント再計算',
          icon: <Calculator className="size-4" />,
          onClick: async () => {
            try {
              const result = await recalcPlaceCounts()
              toast.success(result.message)
            } catch {
              toast.error('再計算に失敗しました')
            }
          },
        },
      ],
    },
  ]

  // 動的タイトル
  const sheetTitle = useMemo(() => {
    if (selectedPlace) return selectedPlace.display_name
    return 'Places'
  }, [selectedPlace])

  if (!hasFullAccess) {
    return <FullAccessFallback />
  }

  return (
    <div className="h-screen w-screen flex flex-col">
      {/* ヘッダー（地図のz-indexより上に配置） */}
      <div className="relative z-1001">
        <Header title="Places" onClose={handleBack} dropdownSections={dropdownSections} />
      </div>

      {/* 地図エリア — z-0で地図内部のz-indexがポータルより下になるよう制御 */}
      <div className="flex-1 relative z-0">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <GoogleMapCanvas
            defaultCenter={DEFAULT_MAP_CENTER}
            defaultZoom={12}
            className="h-full w-full"
            zoomControl={false}
            mapTypeControl
          >
            {/* 表示範囲自動調整 */}
            <MapBoundsUpdater places={places} />

            {/* マップインスタンスを公開 */}
            <MapRefExposer mapRef={mapRef} />

            {/* Placeマーカー（ズームアウト時はクラスタ表示） */}
            <PlaceMarkersLayer
              places={places}
              selectedPlaceId={selectedPlace?.id ?? null}
              onSelectPlace={handleSelectPlace}
            />
          </GoogleMapCanvas>
        )}

        {/* 常時表示のBottomSheet — 一覧/詳細切替 */}
        <BottomSheet
          ref={bottomSheetRef}
          title={sheetTitle}
          open
          onOpenChange={(open) => {
            if (!open) handleSheetClose()
          }}
          snapPoints={SHEET_SNAP_POINTS}
          defaultSnapPoint={SNAP_PEEK}
          disableDismiss
          noOverlay
        >
          {/*
            PlaceListContent は常時マウントして検索/フィルタ/選択などの内部 state を保持する。
            詳細表示中は hidden で非表示にするだけで、× で戻ると同じ状態のまま再表示される。
          */}
          <div hidden={!!selectedPlace}>
            <PlaceListContent
              ref={listRef}
              places={places}
              isLoading={isLoading}
              sort={sort}
              onSortChange={setSort}
              onPlacesChange={setPlaces}
              onSelectPlace={handleSelectPlace}
            />
          </div>
          {selectedPlace && (
            <PlaceSheetContent
              place={selectedPlace}
              onEditClick={() => {
                const target = selectedPlace
                handleBackToList()
                listRef.current?.openReGeocode(target)
              }}
              onUpdated={async () => {
                const data = await getVisitedPlaces(sort)
                setPlaces(data)
                // 選択中のPlaceも最新データで同期
                const updated = data.find((p) => p.id === selectedPlace.id)
                if (updated) setSelectedPlace(updated)
              }}
              onClose={handleBackToList}
            />
          )}
        </BottomSheet>
      </div>
    </div>
  )
}
